import { useMemo } from 'react';
import { Table } from 'antd';
import type { TableProps } from 'antd';
import type { Key } from 'react';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import type { PatientListItem, PatientSex } from '@/models/patient';
import type { Pagination } from '@/models';
import HeartLoader from '@/shared/ui/HeartLoader';
import {
  displayMrn,
  displayPatientName,
  isUnknownMrn,
} from '@/shared/utils/displayPatient';
import { formatName } from '../utils/formatName';
import styles from './PatientTable.module.css';

interface PatientTableProps {
  data?: Pagination<PatientListItem>;
  loading?: boolean;
  page: number;
  pageSize: number;
  onTableChange: TableProps<PatientListItem>['onChange'];
  selectedUuids?: string[];
  onSelectionChange?: (uuids: string[]) => void;
  maxSelection?: number;
}

const SEX_LABEL: Record<PatientSex, string> = {
  MALE: 'Male',
  FEMALE: 'Female',
  OTHER: 'Other',
  '': '—',
};

export default function PatientTable({
  data,
  loading,
  page,
  pageSize,
  onTableChange,
  selectedUuids,
  onSelectionChange,
  maxSelection = 2,
}: PatientTableProps) {
  const navigate = useNavigate();
  const selectionEnabled = !!onSelectionChange;

  // The shared Patient(mrn="UNKNOWN") row is an internal placeholder used by
  // ingestion when DICOM has no PatientID. It belongs only on the Uploads
  // page (where the studies attached to it are flagged for assignment) \u2014 it
  // should never appear as a "real" patient here.
  const visibleResults = useMemo(
    () =>
      (data?.results ?? []).filter((r) => !isUnknownMrn(r.medicalRecordNumber)),
    [data?.results]
  );

  const columns: TableProps<PatientListItem>['columns'] = [
    {
      title: 'Name',
      key: 'name',
      render: (_: unknown, record: PatientListItem) => (
        <div className={styles.nameCell}>
          <span className={styles.patientName}>
            {displayPatientName(record.fullName)}
          </span>
          <span className={styles.mrnLine}>
            MRN: {displayMrn(record.medicalRecordNumber)}
          </span>
        </div>
      ),
    },
    {
      title: 'DOB',
      dataIndex: 'dateOfBirth',
      key: 'dateOfBirth',
      render: (text: string) =>
        text ? moment(text).format('MMM D, YYYY') : '—',
    },
    {
      title: 'Sex',
      dataIndex: 'sex',
      key: 'sex',
      render: (sex: PatientSex) => SEX_LABEL[sex] ?? '—',
    },
    {
      title: 'Referring MD',
      key: 'referringMd',
      render: (_: unknown, record: PatientListItem) =>
        formatName(
          record.latestReferringPhysicianFirstName,
          record.latestReferringPhysicianLastName
        ),
    },
    {
      title: 'Sonographer',
      key: 'sonographer',
      render: (_: unknown, record: PatientListItem) =>
        formatName(
          record.latestSonographerFirstName,
          record.latestSonographerLastName
        ),
    },
    {
      title: 'Date of Latest Study',
      dataIndex: 'latestStudyDate',
      key: 'latestStudyDate',
      render: (text: string) =>
        text ? moment(text).format('MMM D, YYYY') : '—',
    },
  ];

  const rowSelection: TableProps<PatientListItem>['rowSelection'] =
    selectionEnabled
      ? {
          selectedRowKeys: (selectedUuids ?? []) as Key[],
          onChange: (keys) => onSelectionChange?.(keys.map((k) => String(k))),
          // Without this, AntD drops selected keys whose rows aren't in the
          // current dataSource (page change, filter change, sort change),
          // so a selection made on page 1 would silently disappear when
          // the user navigates to page 2 and selects another row.
          preserveSelectedRowKeys: true,
          getCheckboxProps: (record) => {
            const atLimit = (selectedUuids?.length ?? 0) >= maxSelection;
            const isAlreadySelected = selectedUuids?.includes(record.uuid);
            return {
              disabled: atLimit && !isAlreadySelected,
            };
          },
          columnWidth: 48,
        }
      : undefined;

  return (
    <Table<PatientListItem>
      columns={columns}
      dataSource={visibleResults}
      rowKey={(r) => r.uuid}
      loading={{ spinning: !!loading, indicator: <HeartLoader size="sm" /> }}
      onChange={onTableChange}
      bordered={false}
      rowSelection={rowSelection}
      rowClassName={() => styles.clickableRow}
      onRow={(record) => ({
        onClick: (e) => {
          const target = e.target as HTMLElement;
          // Avoid navigating when clicking the selection checkbox cell.
          if (target.closest('.ant-table-selection-column')) return;
          navigate(`/patients/${record.uuid}`);
        },
      })}
      pagination={{
        current: page,
        pageSize,
        total: data?.count,
        showSizeChanger: true,
        showTotal: (t, [s, e]) => `${s}-${e} of ${t}`,
      }}
    />
  );
}
